/**
 * Atelier Couture — AJAX Slide-Out Cart Drawer
 * Connected to Shopify Cart API (/cart.js, /cart/add.js, /cart/change.js)
 */
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.openButtons = document.querySelectorAll('[data-cart-drawer-open]');
    this.closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
    this.body = document.querySelector('.cart-drawer__body');
    this.subtotalEl = document.querySelector('[data-cart-subtotal]');
    this.countBadges = document.querySelectorAll('[data-cart-count]');
    this.freeShippingThreshold = window.AtelierCartSettings?.freeShippingThreshold || 500;

    this.initEvents();
  }

  initEvents() {
    this.openButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Intercept standard Add-to-Cart form submits
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });

    // Delegated events for quantity & remove
    if (this.drawer) {
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          const key = qtyBtn.getAttribute('data-item-key');
          const currentQty = parseInt(qtyBtn.getAttribute('data-current-qty'), 10);
          const delta = parseInt(qtyBtn.getAttribute('data-qty-change'), 10);
          this.changeQuantity(key, currentQty + delta);
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          const key = removeBtn.getAttribute('data-item-key');
          this.changeQuantity(key, 0);
        }

        const upsellBtn = e.target.closest('[data-quick-upsell]');
        if (upsellBtn) {
          const variantId = upsellBtn.getAttribute('data-variant-id');
          this.addVariantDirectly(variantId);
        }
      });

      // Cart Note Handler
      const noteInput = this.drawer.querySelector('[data-cart-notes]');
      if (noteInput) {
        let timeout = null;
        noteInput.addEventListener('input', () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteInput.value })
            });
          }, 600);
        });
      }
    }
  }

  open() {
    if (this.drawer && this.overlay) {
      this.drawer.classList.add('is-open');
      this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.refreshCart();
    }
  }

  close() {
    if (this.drawer && this.overlay) {
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  async handleAddToCart(form) {
    const formData = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        await this.refreshCart();
        this.open();
      } else {
        const err = await response.json();
        alert(err.description || 'Error adding creation to bag.');
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async addVariantDirectly(variantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      await this.refreshCart();
      window.Atelier.toast('Piece added to bag');
    } catch (e) {
      console.error(e);
    }
  }

  async changeQuantity(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (res.ok) {
        await this.refreshCart();
      }
    } catch (e) {
      console.error(e);
    }
  }

  async refreshCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
    } catch (e) {
      console.error(e);
    }
  }

  renderCart(cart) {
    // Update badge count
    this.countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? 'inline-block' : 'none';
    });

    // Update Subtotal
    if (this.subtotalEl) {
      const formatted = (cart.total_price / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      this.subtotalEl.textContent = formatted;
    }

    // Update Free Shipping Bar
    const progressEl = document.querySelector('.free-shipping-bar__progress');
    const msgEl = document.querySelector('[data-free-shipping-text]');
    if (progressEl && msgEl) {
      const currentVal = cart.total_price / 100;
      const targetVal = this.freeShippingThreshold;
      const percent = Math.min(100, Math.round((currentVal / targetVal) * 100));
      progressEl.style.width = `${percent}%`;

      if (currentVal >= targetVal) {
        msgEl.innerHTML = `<span style="color: var(--color-gold-primary);">✓ You qualify for complimentary worldwide insured shipping.</span>`;
      } else {
        const remaining = (targetVal - currentVal).toFixed(2);
        msgEl.innerHTML = `Add <strong>$${remaining}</strong> more for complimentary worldwide insured shipping.`;
      }
    }

    // Render items
    if (!this.body) return;
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div style="text-align: center; padding: 48px 0;">
          <p class="font-serif" style="font-size: 1.5rem; color: #fff;">Your bag is empty.</p>
          <p style="margin-top: 8px;">Explore our embroidered haute couture collections.</p>
        </div>
      `;
      return;
    }

    let itemsHtml = '<div class="cart-items-list">';
    cart.items.forEach(item => {
      itemsHtml += `
        <div class="cart-drawer-item hairline-b" style="display: flex; gap: 16px; padding: 16px 0;">
          <img src="${item.image}" alt="${item.title}" style="width: 80px; height: 106px; object-fit: cover; border: 1px solid #262626;" />
          <div style="flex: 1;">
            <a href="${item.url}" class="font-serif" style="font-size: 1.05rem; display: block; margin-bottom: 4px;">${item.product_title}</a>
            <p style="font-size: 0.75rem; margin-bottom: 8px; color: var(--color-gold-subtle);">${item.variant_title || ''}</p>
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div style="display: inline-flex; align-items: center; border: 1px solid #333;">
                <button type="button" data-qty-change="-1" data-item-key="${item.key}" data-current-qty="${item.quantity}" style="background: transparent; border: none; color: #fff; padding: 4px 10px; cursor: pointer;">-</button>
                <span style="font-size: 0.8rem; padding: 0 8px;">${item.quantity}</span>
                <button type="button" data-qty-change="1" data-item-key="${item.key}" data-current-qty="${item.quantity}" style="background: transparent; border: none; color: #fff; padding: 4px 10px; cursor: pointer;">+</button>
              </div>
              <span style="font-size: 0.9rem; font-weight: 500;">$${(item.final_line_price / 100).toFixed(2)}</span>
            </div>
            <button type="button" data-cart-remove data-item-key="${item.key}" style="background: transparent; border: none; color: #666; font-size: 0.7rem; text-decoration: underline; margin-top: 8px; cursor: pointer;">Remove</button>
          </div>
        </div>
      `;
    });
    itemsHtml += '</div>';
    this.body.innerHTML = itemsHtml;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.atelierCart = new CartDrawer();
});
